import React, { useRef, useState } from "react";
import { uploadChunked as uploadChunkedLib } from "../lib/upload";
import { FileText, Download, ExternalLink, Trash2, Upload, Camera, FolderOpen, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { fmtDT } from "../lib/date";

/* 分包合同运营链共用组件：验收资料类型 / 开工状态 / 已签合同链接 / 分片上传 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 5 * 1024 * 1024;

export const DOC_TYPES = {
  photo: { zh: "完工照片", en: "Completion photo", cls: "bg-sky-100 text-sky-700" },
  warranty: { zh: "质保书", en: "Warranty", cls: "bg-violet-100 text-violet-700" },
  report: { zh: "检测报告", en: "Test report", cls: "bg-amber-100 text-amber-700" },
  acceptance: { zh: "验收单", en: "Acceptance form", cls: "bg-emerald-100 text-emerald-700" },
  other: { zh: "其他", en: "Other", cls: "bg-slate-100 text-slate-600" },
};
export const SW_STATUS = {
  pending: { zh: "待分包商确认", en: "Awaiting confirmation", cls: "bg-sky-100 text-sky-700" },
  confirmed: { zh: "分包商已确认开工", en: "Start confirmed", cls: "bg-emerald-100 text-emerald-700" },
  reschedule_requested: { zh: "分包商申请改期", en: "Reschedule requested", cls: "bg-amber-100 text-amber-800" },
};
export const docTypeOf = (p) => (DOC_TYPES[p.docType] ? p.docType : p.kind === "image" ? "photo" : "other");
export const usd = (s) => { if (!s) return "—"; const [y, m, d] = String(s).slice(0, 10).split("-"); return m && d ? `${m}/${d}/${y}` : s; };
export const contractPdfUrl = (sid, token, dl) => `${BACKEND}/api/subcontracts/${sid}/contract-preview.pdf?auth=${token}${dl ? "&dl=1" : ""}`;

/* 铃铛直达：滚到区块并高亮 2.5s；sticky=true（铃铛点入 highlight=1）→ 保持高亮直到用户在该区块内操作 */
const RING = ["ring-2", "ring-amber-400", "ring-offset-2"];
export function focusSection(testid, sticky = false) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add(...RING, "rounded-md", "transition-shadow");
  const clear = () => { el.classList.remove(...RING); el.removeAttribute("data-highlight"); ["pointerdown", "keydown", "change"].forEach((ev) => el.removeEventListener(ev, clear)); };
  if (!sticky) { setTimeout(clear, 2500); return true; }
  el.setAttribute("data-highlight", "1");
  ["pointerdown", "keydown", "change"].forEach((ev) => el.addEventListener(ev, clear));
  return true;
}
/* 铃铛点击跳转：分包深链附加 highlight=1（保持高亮直到操作） */
export const bellLink = (link) => (link && /[?&]sub=/.test(link) && !/[?&]highlight=/.test(link) ? `${link}&highlight=1` : link);
export const parseDeepLink = (link) => { const q = new URLSearchParams(String(link || "").split("?")[1] || ""); return { sub: q.get("sub"), section: q.get("section"), highlight: q.get("highlight") === "1" }; };
export const PORTAL_SECTION = { esign: "portal-esign", startwork: "portal-startwork-section", quote: "portal-quote", stages: "portal-stages", completion: "portal-completion", rects: "portal-rects", pay: "portal-pay", checkin: "portal-checkin", tasks: "portal-tasks-section", card: "portal-sub" };
export const TEAM_SECTION = { esign: "sub-esign", startwork: "sub-startwork", quote: "sub-quote-review", completion: "sub-completion-view", tasks: "sub-taskchain", stages: "sub-stages-team", payments: "sub-payplan", change_orders: "sub-sco-panel", timeline: "sub-timeline" };

/* 在申请表单内直接补件（验收单 / 质保书 / 完工照片） */
export function GapUploadButtons({ gaps, zh, busy, onUpload, testidPrefix }) {
  const types = [...new Set((gaps || []).flatMap((g) => (g === "photo_or_acceptance" ? ["photo", "acceptance"] : [g])))].filter((t) => DOC_TYPES[t]);
  if (!types.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-gap-uploads`}>
      <span className="text-[10px] text-slate-500">{zh ? "在此直接补件：" : "Attach here:"}</span>
      {types.map((t) => (
        <label key={t} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-dashed border-amber-300 bg-white text-[11px] text-amber-800 hover:border-amber-500 cursor-pointer">
          <input type="file" className="hidden" multiple disabled={busy} accept={t === "photo" ? "image/*" : "image/*,application/pdf,.doc,.docx"} data-testid={`${testidPrefix}-gap-input-${t}`} onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) onUpload(fs, t); }} />
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{zh ? `上传${DOC_TYPES[t].zh}` : `Upload ${DOC_TYPES[t].en.toLowerCase()}`}
        </label>
      ))}
    </div>
  );
}

export async function uploadChunked(api, file) {
  const data = await uploadChunkedLib(api, file);
  return { fileId: data.fileId, filename: data.filename, contentType: data.contentType, kind: data.kind };
}

export const GAP_LABEL = {
  acceptance: { zh: "验收单", en: "acceptance form" },
  warranty: { zh: "质保书", en: "warranty" },
  photo_or_acceptance: { zh: "完工照片 / 验收单", en: "completion photo / acceptance form" },
};
export const gapNames = (gaps, zh) => (gaps || []).map((g) => (GAP_LABEL[g] ? (zh ? GAP_LABEL[g].zh : GAP_LABEL[g].en) : g)).join(zh ? "、" : ", ");

/* 验收缺件提示（不阻断）：付款审批页 + 分包商申请进度款共用 */
export function EvidenceGapWarning({ gaps, isFinal, zh, testid, onGoUpload }) {
  if (!gaps?.length) return null;
  const names = gapNames(gaps, zh);
  return (
    <p data-testid={testid} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1.5 flex-wrap">
      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
      <span>{isFinal ? (zh ? `尾款申请缺少：${names}` : `Final payment is missing: ${names}`) : (zh ? `该阶段已验收关闭但缺少：${names}` : `Stage closed but missing: ${names}`)}{!onGoUpload && (zh ? "，建议审批前向分包商补齐" : " — consider requesting before approval")}</span>
      {onGoUpload && <button type="button" onClick={onGoUpload} data-testid={`${testid}-go`} className="underline font-semibold hover:text-amber-950">{zh ? "去上传 →" : "Upload now →"}</button>}
    </p>
  );
}

export function DocTypeSelect({ value, onChange, zh, testid, className = "" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`text-xs border border-slate-200 rounded-md px-2 bg-white ${className}`}>
      {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{zh ? v.zh : v.en}</option>)}
    </select>
  );
}

export function StartWorkBadge({ sw, zh, testid }) {
  if (!sw?.plannedDate) return null;
  const s = SW_STATUS[sw.status] || SW_STATUS.pending;
  return <span data-testid={testid} data-status={sw.status} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${s.cls}`}>{zh ? "开工" : "Start"} {usd(sw.plannedDate)} · {zh ? s.zh : s.en}</span>;
}

export function SignedContractLinks({ sc, token, zh, testidPrefix }) {
  const signed = sc.signedContract || {};
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]" data-testid={`${testidPrefix}-signed-links`}>
      <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
      <span className="text-green-700 font-medium">{zh ? `已签署 · ${sc.esignSignedBy || ""}` : `Signed by ${sc.esignSignedBy || ""}`}{signed.signerTitle ? ` · ${signed.signerTitle}` : ""}{sc.esignSignedAt ? ` · ${fmtDT(sc.esignSignedAt)}` : ""}</span>
      <a href={contractPdfUrl(sc.id, token)} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-signed-preview`} className="inline-flex items-center gap-0.5 text-brand-500 hover:underline font-medium"><ExternalLink className="h-3 w-3" />{zh ? "预览已签合同" : "Preview signed PDF"}</a>
      <a href={contractPdfUrl(sc.id, token, true)} data-testid={`${testidPrefix}-signed-download`} className="inline-flex items-center gap-0.5 text-slate-600 hover:underline"><Download className="h-3 w-3" />{zh ? "下载" : "Download"}</a>
      {signed.recordHash && <span className="font-mono text-[9px] text-slate-400" title={signed.recordHash}>#{String(signed.recordHash).slice(0, 12)}</span>}
    </div>
  );
}

export function CompletionDocList({ items = [], fileUrl, zh, testidPrefix = "cdoc", onRelabel, onDelete, onOpenImage, onOpenGallery }) {
  if (!items.length) return null;
  // 付款照片查看器按页面上资料分类的显示顺序翻页，且只收集图片，避免翻到 PDF 等文件。
  const galleryImages = onOpenGallery
    ? Object.keys(DOC_TYPES).flatMap((type) => items.filter((item) => docTypeOf(item) === type && item.kind === "image"))
    : [];
  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-list`}>
      {Object.keys(DOC_TYPES).map((k) => {
        const group = items.filter((p) => docTypeOf(p) === k);
        if (!group.length) return null;
        const t = DOC_TYPES[k];
        return (
          <div key={k} data-testid={`${testidPrefix}-group-${k}`}>
            <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full mb-1 ${t.cls}`}>{zh ? t.zh : t.en} · {group.length}</span>
            <div className="flex flex-wrap gap-1.5">
              {group.map((p) => (
                <div key={p.id} className="relative" data-testid={`${testidPrefix}-item-${p.id}`} title={p.milestoneName ? `${p.filename} · ${p.milestoneName}` : p.filename}>
                  {p.kind === "image"
                    ? <button type="button" title={p.filename} onClick={() => {
                      if (onOpenGallery) {
                        const index = galleryImages.findIndex((item) => item === p);
                        if (index >= 0) return onOpenGallery(galleryImages, index);
                      }
                      return onOpenImage ? onOpenImage(fileUrl(p.wmFileId || p.fileId)) : window.open(fileUrl(p.wmFileId || p.fileId), "_blank");
                    }} data-wm={p.wmFileId ? 1 : 0} className={`h-14 w-14 rounded-md overflow-hidden border bg-white block ${p.published ? "ring-1 ring-green-400 border-green-300" : "border-slate-200"}`}><img src={fileUrl(p.wmFileId || p.fileId)} alt={p.filename} className="h-full w-full object-cover" /></button>
                    : <a href={fileUrl(p.fileId)} target="_blank" rel="noreferrer" title={p.filename} className="h-14 w-24 rounded-md border border-slate-200 bg-white flex flex-col items-center justify-center px-1 text-slate-500 hover:border-brand-400"><FileText className="h-4 w-4 text-red-400" /><span className="text-[9px] truncate w-full text-center">{p.filename}</span></a>}
                  {p.milestoneName && <span data-testid={`${testidPrefix}-stage-${p.id}`} className="absolute bottom-0 left-0 right-0 bg-slate-900/70 text-white text-[8px] leading-tight px-1 truncate rounded-b-md pointer-events-none">{p.wmFileId ? "WM · " : ""}{p.milestoneName}</span>}
                  {!p.milestoneName && p.wmFileId && <span data-testid={`${testidPrefix}-wm-${p.id}`} className="absolute bottom-0 left-0 right-0 bg-emerald-700/80 text-white text-[8px] leading-tight text-center rounded-b-md pointer-events-none">WM</span>}
                  {onRelabel && <DocTypeSelect value={docTypeOf(p)} onChange={(v) => onRelabel(p, v)} zh={zh} testid={`${testidPrefix}-type-${p.id}`} className="mt-0.5 block w-full max-w-[96px] !text-[9px] !px-0.5 h-5" />}
                  {onDelete && !p.published && <button type="button" title={zh ? "删除" : "Delete"} onClick={() => onDelete(p)} data-testid={`${testidPrefix}-del-${p.id}`} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-white border border-slate-300 text-slate-500 hover:text-red-600 flex items-center justify-center"><Trash2 className="h-2.5 w-2.5" /></button>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 完工资料保留独立相机与设备选择：只有相机 input 带 capture，手机相册可多选。
 * 付款入口额外提供当前项目图库，资料分类随选择传回；其他调用方保持原有上传入口。
 */
export function CompletionUploader({ zh, busy, onUpload, testidPrefix = "cdoc", camera = false, onPickProject, projectDisabled = false }) {
  const [type, setType] = useState("photo");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const pick = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) onUpload(fs, type); };
  const t = DOC_TYPES[type];
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-uploader`}>
      <label className={onPickProject ? "w-full flex items-center gap-2 text-slate-600" : ""}>{onPickProject && (zh ? "资料类型" : "Document type")}<DocTypeSelect value={type} onChange={setType} zh={zh} testid={`${testidPrefix}-doctype`} className={onPickProject ? "h-11" : "h-8"} /></label>
      <input ref={fileRef} type="file" accept={type === "photo" ? "image/*" : "image/*,application/pdf,.doc,.docx,.xls,.xlsx"} multiple disabled={busy} className="hidden" data-testid={`${testidPrefix}-input`} onChange={pick} />
      {camera && <input ref={camRef} type="file" accept="image/*" capture="environment" disabled={busy} className="hidden" data-testid={`${testidPrefix}-camera-input`} onChange={pick} />}
      {camera && (
        <Button type="button" data-testid={`${testidPrefix}-camera`} size="sm" disabled={busy} className={`text-xs ${onPickProject ? "h-11 flex-1 sm:flex-none" : "h-8"} bg-brand-500 hover:bg-brand-600 text-white`} onClick={() => camRef.current?.click()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Camera className="h-3.5 w-3.5 mr-1" />{zh ? "拍照上传" : "Take photo"}</>}
        </Button>
      )}
      <Button type="button" data-testid={`${testidPrefix}-upload`} size="sm" variant="outline" disabled={busy} className={`text-xs ${onPickProject ? "h-11 flex-1 sm:flex-none" : "h-8"}`} onClick={() => fileRef.current?.click()}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" />{onPickProject ? (zh ? (type === "photo" ? "相册 / 文件" : "选择文件") : (type === "photo" ? "Album / files" : "Choose files")) : (zh ? `上传${t.zh}` : `Upload ${t.en.toLowerCase()}`)}</>}
      </Button>
      {onPickProject && <Button type="button" data-testid={`${testidPrefix}-project`} size="sm" variant="outline" disabled={busy || projectDisabled} className="h-11 w-full text-xs sm:w-auto" onClick={() => onPickProject(type)}><FolderOpen className="mr-1 h-3.5 w-3.5" />{zh ? "从工地现场照片选择" : "From Job Site Photos"}</Button>}
    </div>
  );
}
